import React from 'react';
import { Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { outlinedRedBtn } from '../EditLegalListWizard/StepButtonBar';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import { SelectOption } from '../../../../../Components/Form/Form.types';

const PRIMARY = '#c4122f';
const GREEN = '#4caf50';

interface Step5LawNewCompanyUnitPreviewProps {
  selectedLaw?: { id: number; name: string; number: string };
  selectedCompanyUnits: SelectOption[];
  selectedManagers: SelectOption[];
  onSaveAndQuit: () => Promise<void>;
  onSaveAndNewConnection: () => Promise<void>;
  isSaving?: boolean;
}

const Step5LawNewCompanyUnitPreview = ({
  onSaveAndQuit,
  onSaveAndNewConnection,
  isSaving = false,
  selectedLaw,
  selectedCompanyUnits,
  selectedManagers,
}: Step5LawNewCompanyUnitPreviewProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(2) : theme.spacing(3);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ width: '100%', maxHeight: '360px', overflowY: 'auto', padding: paperPadding, backgroundColor: '#f7f0f1', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        {selectedLaw && (
          <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: theme.spacing(2) }}>
            <Typography style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
              {formatMessage({ id: 'law-portal.manage.law-list.table.law' })}
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', backgroundColor: '#fff', borderRadius: '4px' }}>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', backgroundColor: '#e3f2fd', borderRadius: '6px', flexShrink: 0 }}>
                <DescriptionOutlinedIcon style={{ color: '#1976d2', fontSize: '24px' }} />
              </Box>
              <Typography variant="body2" style={{ fontWeight: 500, fontSize: '14px', color: '#222' }}>
                {selectedLaw.number} – {selectedLaw.name}
              </Typography>
            </Box>
          </Box>
        )}

        {selectedCompanyUnits.length > 0 && (
          <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: theme.spacing(2) }}>
            <Typography style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
              {formatMessage({ id: 'form.selectCompanyUnits' })} ({selectedCompanyUnits.length})
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {selectedCompanyUnits.map((unit) => (
                <Box key={String(unit.value)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', backgroundColor: '#fff', borderRadius: '4px' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', backgroundColor: GREEN, borderRadius: '6px', flexShrink: 0 }}>
                    <BusinessOutlinedIcon style={{ color: '#fff', fontSize: '24px' }} />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 500, color: '#222' }}>{unit.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {selectedManagers.length > 0 && (
          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
              {formatMessage({ id: 'law-portal.manage.edit-law.complianceUsers' })} ({selectedManagers.length})
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {selectedManagers.map((manager) => (
                <Box key={String(manager.value)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', backgroundColor: '#fff', borderRadius: '4px' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', backgroundColor: GREEN, borderRadius: '6px', flexShrink: 0 }}>
                    <PersonOutlineIcon style={{ color: '#fff', fontSize: '24px' }} />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 500, color: '#222' }}>{manager.label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </div>
      <Box style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexShrink: 0 }}>
        <Button
          variant="outlined"
          onClick={onSaveAndNewConnection}
          disabled={isSaving}
          style={{ ...outlinedRedBtn, minWidth: '180px' }}
        >
          {formatMessage({ id: 'form.save-and-new-connection' })}
        </Button>
        <Button
          variant="contained"
          onClick={onSaveAndQuit}
          disabled={isSaving}
          style={{ backgroundColor: PRIMARY, color: '#fff', minWidth: '160px' }}
        >
          {isSaving ? <CircularProgress size={20} /> : formatMessage({ id: 'form.save-and-quit' })}
        </Button>
      </Box>
    </div>
  );
};

export default Step5LawNewCompanyUnitPreview;
